import dayjs from "dayjs";
import { Button, buttonVariants } from "../ui/button";
import { Clock3, PlayCircle, UserRound, Video } from "lucide-react";
import "dayjs/locale/fr";
import { cn } from "@/lib/utils";
dayjs.locale("fr");

interface Props {
  titre: string;
  date: string;
  intervenants: string | null;
  replayUrl: string | null;
  liveUrl: string | null;
  description: string | null;
  heureDebut: string | null;
  heureOuvertureAcces: string | null;
  disableLive?: boolean;
}

export default function WebinaireItem({
  titre,
  intervenants,
  date,
  replayUrl,
  liveUrl,
  description,
  heureDebut,
  heureOuvertureAcces,
  disableLive = false,
}: Props) {
  const isPast = dayjs(date).endOf("day").isBefore(dayjs());
  const day = dayjs(date).format("DD");
  const month = dayjs(date).format("MMMM");
  const year = dayjs(date).format("YYYY");

  return (
    <article className="grid gap-5 px-5 py-6 sm:grid-cols-[7rem_minmax(0,1fr)] sm:px-6">
      <time
        dateTime={date}
        className="flex h-fit items-baseline justify-center gap-2 rounded-xl bg-[#fbfaf6] px-4 py-3 text-[#173f35] sm:flex-col sm:items-center sm:gap-0 sm:py-4"
      >
        <span className="text-3xl font-bold leading-none tabular-nums">{day}</span>
        <span className="text-sm font-semibold capitalize">{month}</span>
        <span className="text-xs text-slate-500">{year}</span>
      </time>

      <div className="min-w-0">
        <h3 className="text-lg font-bold leading-6 text-slate-950 sm:text-xl">{titre}</h3>

        {intervenants && (
          <div className="mt-2 flex items-start gap-2 text-sm text-slate-500">
            <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <span>{intervenants}</span>
          </div>
        )}

        {description && <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">{description}</p>}

        {!isPast && (heureOuvertureAcces || heureDebut) && (
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {heureOuvertureAcces && (
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-[#286351]" />
                Accès dès {heureOuvertureAcces}
              </div>
            )}
            {heureDebut && (
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-[#286351]" />
                Début à {heureDebut}
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          {isPast ? (
            replayUrl ? (
              <a
                href={replayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ size: "sm" }),
                  "w-full gap-2 bg-[#173f35] hover:bg-[#23594a] sm:w-auto",
                )}
              >
                <PlayCircle className="h-4 w-4" />
                Regarder le replay
              </a>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock3 className="h-4 w-4" />
                Replay bientôt disponible
              </div>
            )
          ) : liveUrl ? (
            disableLive ? (
              <Button
                className="w-full bg-[#173f35] sm:w-auto"
                disabled
                icon={<Video className="h-4 w-4" />}
                size="sm"
              >
                Voir en direct
              </Button>
            ) : (
              <a
                href={liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ size: "sm" }),
                  "w-full gap-2 bg-[#173f35] hover:bg-[#23594a] sm:w-auto",
                )}
              >
                <Video className="h-4 w-4" />
                Voir en direct
              </a>
            )
          ) : (
            <div className="flex items-start gap-2 text-sm leading-5 text-slate-400">
              <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
              Le lien du direct sera disponible et envoyé par email sept jours avant le webinaire.
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
